/**
 * PlayerMiniList — compact player roster list used inside PositionCard disclosures.
 *
 * Extracted from SchoolDetailPage.tsx (legacy PlayerMiniList, lines 141–170).
 * Preserves the same visual treatment: jersey column, name + (RS) marker, class-year column.
 * Sort: SR → GR → JR → SO → FR, then alpha-by-name within each group.
 * Hometown is NOT shown (parity with original).
 */
import React from 'react';
import type { PlayerDetail } from '../../api/school';

const CLASS_YEAR_PRIORITY: Record<string, number> = {
  SR: 1, GR: 2, JR: 3, SO: 4, FR: 5,
};

function classYearPriority(cy: string): number {
  return CLASS_YEAR_PRIORITY[cy] ?? 99;
}

interface PlayerMiniListProps {
  players: PlayerDetail[];
}

export function PlayerMiniList({ players }: PlayerMiniListProps) {
  const sorted = [...players].sort((a, b) => {
    const cyDiff = classYearPriority(a.class_year) - classYearPriority(b.class_year);
    if (cyDiff !== 0) return cyDiff;
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="bg-surface-sunken rounded mx-1 my-1 px-2 py-1">
      <div className="flex text-[10px] font-semibold uppercase tracking-widest text-gray-600 pb-1">
        <span className="w-8">#</span>
        <span className="flex-1">Name</span>
        <span className="w-8 text-right">Yr</span>
      </div>
      {sorted.length === 0 ? (
        <p className="text-sm text-gray-500 italic px-0 py-2">No players on record at this position.</p>
      ) : (
        sorted.map((p) => (
          <div key={p.player_id} className="flex items-baseline text-sm border-t border-hairline py-1">
            <span className="w-8 tabular-nums text-gray-700">{p.jersey_number ?? '—'}</span>
            <span className="flex-1 text-gray-900">
              {p.name}
              {p.is_redshirt && <span className="text-xs text-gray-500 ml-1">(RS)</span>}
            </span>
            <span className="w-8 text-right tabular-nums text-gray-700">{p.class_year}</span>
          </div>
        ))
      )}
    </div>
  );
}
